import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Lightning, ArrowRight, Spinner } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PostBumpUpsellModal from "@/components/PostBumpUpsellModal";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling"); // polling | paid | error
    const [attempts, setAttempts] = useState(0);
    const [tier, setTier] = useState("founder");
    const [showUpsell, setShowUpsell] = useState(false);
    const [upsellEligibleUntil, setUpsellEligibleUntil] = useState(null);
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const startedRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        if (startedRef.current) return;
        startedRef.current = true;

        let cancelled = false;
        const poll = async (n = 0) => {
            if (cancelled) return;
            if (n >= 10) {
                setStatus("error");
                return;
            }
            setAttempts(n + 1);
            try {
                const r = await api.get(`/stripe/status/${sessionId}`);
                if (r.data.tier) setTier(r.data.tier);
                if (r.data.payment_status === "paid") {
                    const fresh = await refresh();
                    setStatus("paid");
                    // Trigger post-bump Lifetime upsell if eligibility window is active and unused
                    if (
                        fresh &&
                        !fresh.lifetime_member &&
                        !fresh.lifetime_upsell_used &&
                        fresh.lifetime_upsell_eligible_until &&
                        new Date(fresh.lifetime_upsell_eligible_until).getTime() > Date.now()
                    ) {
                        setUpsellEligibleUntil(fresh.lifetime_upsell_eligible_until);
                        // Brief delay so the success card renders first
                        setTimeout(() => setShowUpsell(true), 1200);
                    }
                    return;
                }
                if (r.data.status === "expired") {
                    setStatus("error");
                    return;
                }
                setTimeout(() => poll(n + 1), 2000);
            } catch (e) {
                // Transient network errors are expected during polling — keep retrying
                // up to the 10-attempt cap. The error UI takes over only at exhaustion.
                if (e?.response?.status && e.response.status >= 500) {
                    // 5xx: log so prod monitoring can see real backend issues
                    console.warn("Payment status polling 5xx:", e.response.status);
                }
                setTimeout(() => poll(n + 1), 2000);
            }
        };
        poll(0);
        return () => {
            cancelled = true;
        };
    }, [sessionId, refresh]);

    const isLifetime = tier === "lifetime";

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-6 grid-bg relative">
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background pointer-events-none" />
            <div className="relative w-full max-w-2xl">
                <div className="flex items-center gap-2 justify-center mb-8">
                    <div className="w-10 h-10 bg-electric-blue flex items-center justify-center neon-border">
                        <Lightning weight="fill" size={22} className="text-white" />
                    </div>
                    <span className="font-heading font-black tracking-tighter text-2xl">
                        NEXUS<span className="text-electric-blue">.</span>LAB
                    </span>
                </div>

                <div className="glass-panel p-10 relative overflow-hidden text-center">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue to-transparent" />

                    {status === "polling" && (
                        <div data-testid="checkout-polling">
                            <Spinner size={48} weight="bold" className="text-electric-blue mx-auto mb-4 animate-spin" />
                            <h1 className="font-heading text-3xl font-black tracking-tight mb-3">
                                Verifying payment...
                            </h1>
                            <p className="text-muted-foreground">
                                Confirming your seat in the Founder cohort. Attempt {attempts}/10.
                            </p>
                        </div>
                    )}

                    {status === "paid" && (
                        <div data-testid="checkout-success">
                            <CheckCircle size={64} weight="fill" className="text-electric-blue mx-auto mb-4" />
                            <p className="label-mono text-electric-blue mb-3">
                                {isLifetime ? "LIFETIME ACCESS UNLOCKED" : "SEAT CONFIRMED"}
                            </p>
                            <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight mb-4">
                                {isLifetime ? (
                                    <>The Vault is <span className="text-electric-blue">yours</span>.</>
                                ) : (
                                    <>You're <span className="text-electric-blue">in</span>.</>
                                )}
                            </h1>
                            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                                {isLifetime
                                    ? "Welcome to the LIFETIME FOUNDER tier. Every Recipe, every Playbook, every future drop — yours forever. No subscription. Ever."
                                    : "Welcome to the Nexus Creator Lab Founder cohort. Your $20/mo rate is locked. Forever."}
                            </p>
                            <button
                                onClick={() => navigate("/dashboard")}
                                data-testid="success-enter-dashboard-btn"
                                className="inline-flex items-center justify-center gap-2 bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-8 py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_32px_rgba(0,123,255,0.6)]"
                            >
                                ENTER THE LAB
                                <ArrowRight size={18} weight="bold" />
                            </button>
                        </div>
                    )}

                    {status === "error" && (
                        <div data-testid="checkout-error">
                            <h1 className="font-heading text-3xl font-black tracking-tight mb-3">
                                Payment status timed out
                            </h1>
                            <p className="text-muted-foreground mb-6">
                                If you completed payment, check your email or contact support.
                            </p>
                            <button
                                onClick={() => navigate("/checkout/cancel")}
                                data-testid="error-retry-btn"
                                className="inline-flex items-center gap-2 border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-6 py-3 text-sm tracking-wider uppercase transition-all"
                            >
                                Retry Checkout
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {showUpsell && upsellEligibleUntil && (
                <PostBumpUpsellModal
                    eligibleUntil={upsellEligibleUntil}
                    onDismiss={() => setShowUpsell(false)}
                />
            )}
        </div>
    );
}
